import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Radio, BookOpen, TrendingUp, Cpu } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { WeatherStations } from './WeatherStations';

interface DashboardData {
  weekly_signals: number;
  monthly_analyses: number;
  monthly_predictions: number;
  severity_distribution: Array<{ severity: string; count: number }>;
  top_categories: Array<{ category: string; count: number }>;
  llm_provider: string;
}

// Halo signal palette mapped to severity levels
const SEVERITY_TONE: Record<string, string> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'success',
};

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#FF3A5C',
  high: '#F5D547',
  medium: '#3DD7E5',
  low: '#2BE08C',
};

// Minimal inline sparkline (static shape — replace with live data when available)
function Sparkline({ tone }: { tone: string }) {
  const points = [4, 7, 5, 9, 6, 11, 8, 12, 9, 14];
  const max = Math.max(...points);
  const w = 96;
  const h = 32;
  const xs = points.map((_, i) => (i / (points.length - 1)) * w);
  const ys = points.map((v) => h - (v / max) * h);
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x},${ys[i]}`).join(' ');
  return (
    <svg className="stat-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <polyline points={xs.map((x, i) => `${x},${ys[i]}`).join(' ')} />
    </svg>
  );
}

export function Dashboard() {
  const { data, loading, error } = useApi<DashboardData>('/api/dashboard');

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-16)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
        Loading intelligence data…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 'var(--space-16)', textAlign: 'center', color: 'var(--color-danger)' }}>
        Failed to load dashboard: {error}
      </div>
    );
  }

  const statTiles = [
    { label: 'Signals', sublabel: 'Last 7 days', value: data.weekly_signals, tone: 'info', Icon: Radio },
    { label: 'Analyses', sublabel: 'Last 30 days', value: data.monthly_analyses, tone: 'success', Icon: BookOpen },
    { label: 'Predictions', sublabel: 'Last 30 days', value: data.monthly_predictions, tone: 'warning', Icon: TrendingUp },
    { label: 'LLM Engine', sublabel: 'Active provider', value: data.llm_provider, tone: 'neutral', Icon: Cpu },
  ];

  return (
    <div className="stack" style={{ gap: 'var(--space-8)' }}>
      {/* Page header */}
      <div>
        <span className="eyebrow">Intelligence Dashboard</span>
        <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)' }}>
          Sentinel — Maldives Geopolitical Monitor
        </h1>
      </div>

      {/* Stat tiles */}
      <div className="grid-4">
        {statTiles.map(({ label, sublabel, value, tone, Icon }) => (
          <div key={label} className="stat-tile" data-tone={tone}>
            <div className="stat-head">
              <span className="stat-eyebrow">{label}</span>
              <Icon size={14} strokeWidth={1.75} color="var(--color-text-muted)" />
            </div>
            <div className="stat-value">{value}</div>
            <div className="stat-meta">
              <span className="stat-foot">{sublabel}</span>
              <Sparkline tone={tone} />
            </div>
          </div>
        ))}
      </div>

      {/* Weather stations */}
      <WeatherStations />

      {/* Charts row */}
      <div className="grid-2" style={{ gap: 'var(--space-6)' }}>
        {/* Severity bar chart */}
        <div className="card">
          <div className="card-eyebrow">Signal Severity</div>
          <div className="card-title">Distribution — last 7 days</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.severity_distribution} barSize={28}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis dataKey="severity" tick={{ fill: 'var(--color-text-muted)', fontSize: 11, fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11, fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border-strong)', borderRadius: 10, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-primary)' }}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.severity_distribution.map((entry) => (
                  <Cell key={entry.severity} fill={SEVERITY_COLOR[entry.severity] || '#5B6BFF'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top categories */}
        <div className="card">
          <div className="card-eyebrow">Top Categories</div>
          <div className="card-title">Active issue clusters</div>
          <div className="stack" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
            {data.top_categories.length === 0 && (
              <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>No data yet — run the signal detector to populate.</p>
            )}
            {data.top_categories.map((cat, i) => {
              const tones = ['info', 'success', 'warning', 'danger', 'neutral'];
              const tone = tones[i % tones.length];
              return (
                <div key={cat.category} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--color-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm-size)', textTransform: 'capitalize', color: 'var(--color-text-primary)' }}>
                    {cat.category.replace(/_/g, ' ')}
                  </span>
                  <span className="chip" data-tone={tone}>{cat.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
